const STORAGE_KEY = 'rememberedAccounts';
const LEGACY_KEY = 'rememberedAccount';

/** Kein fest verdrahteter Hosting-Default — Provider oder manuelle IMAP/SMTP-Angabe. */
export const DEFAULT_MAIL_SERVER = {
  imapHost: '',
  smtpHost: '',
  imapPort: 993,
  smtpPort: 465,
};

const MAX_ACCOUNTS = 12;

function normalizeHost(host, fallback = DEFAULT_MAIL_SERVER.imapHost) {
  const cleaned = String(host || '')
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, '')
    .replace(/\/+$/, '');
  if (cleaned) return cleaned;
  return String(fallback || '')
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, '')
    .replace(/\/+$/, '');
}

function normalizePort(value, fallback) {
  const n = parseInt(value, 10);
  if (!Number.isFinite(n) || n < 1 || n > 65535) return fallback;
  return n;
}

export function normalizeMailServer(mail = {}) {
  const legacyHost = mail.host || mail.mailHost || '';
  const imapHost = normalizeHost(
    mail.imapHost ?? mail.imap_host ?? legacyHost,
    DEFAULT_MAIL_SERVER.imapHost
  );
  const smtpHost = normalizeHost(
    mail.smtpHost ?? mail.smtp_host ?? (legacyHost || imapHost),
    imapHost
  );
  return {
    imapHost,
    smtpHost,
    imapPort: normalizePort(mail.imapPort ?? mail.imap_port, DEFAULT_MAIL_SERVER.imapPort),
    smtpPort: normalizePort(mail.smtpPort ?? mail.smtp_port, DEFAULT_MAIL_SERVER.smtpPort),
    host: imapHost,
  };
}

export function accountKey(email) {
  return String(email || '').trim().toLowerCase();
}

function normalizeAccount(raw) {
  if (!raw || typeof raw.email !== 'string') return null;
  const email = accountKey(raw.email);
  if (!email) return null;
  const mail = normalizeMailServer(raw.mail || raw);
  const authType = raw.authType === 'google' ? raw.authType : 'password';
  return {
    id: email,
    email,
    password: typeof raw.password === 'string' ? raw.password : '',
    authType,
    mail,
    lastUsedAt: raw.lastUsedAt || raw.updatedAt || null,
  };
}

function readLegacyAccount() {
  try {
    const raw = localStorage.getItem(LEGACY_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return normalizeAccount(parsed);
  } catch (_) {
    return null;
  }
}

function readAccountsFromStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const list = Array.isArray(parsed) ? parsed : parsed?.accounts;
    if (!Array.isArray(list)) return null;
    return list.map(normalizeAccount).filter(Boolean);
  } catch (_) {
    return null;
  }
}

function mergeByEmail(accounts) {
  const map = new Map();
  for (const account of accounts) {
    if (!account?.email) continue;
    const prev = map.get(account.email);
    if (!prev) {
      map.set(account.email, account);
      continue;
    }
    const prevTime = Date.parse(prev.lastUsedAt || '') || 0;
    const nextTime = Date.parse(account.lastUsedAt || '') || 0;
    const newer = nextTime >= prevTime ? account : prev;
    const older = nextTime >= prevTime ? prev : account;
    // Passwort vom neueren behalten, sonst vom älteren übernehmen
    if (!newer.password && older.password) newer.password = older.password;
    map.set(account.email, newer);
  }
  return Array.from(map.values()).sort((a, b) => {
    const aTime = Date.parse(a.lastUsedAt || '') || 0;
    const bTime = Date.parse(b.lastUsedAt || '') || 0;
    return bTime - aTime;
  });
}

export const getRememberedAccounts = () => {
  const fromStorage = readAccountsFromStorage();
  const legacy = readLegacyAccount();

  if (fromStorage && fromStorage.length) {
    const merged = mergeByEmail(legacy ? [...fromStorage, legacy] : fromStorage);
    if (legacy) {
      try {
        localStorage.removeItem(LEGACY_KEY);
      } catch (_) {}
      saveRememberedAccounts(merged);
    }
    return merged;
  }

  if (legacy) {
    const list = saveRememberedAccounts([legacy]);
    try {
      localStorage.removeItem(LEGACY_KEY);
    } catch (_) {}
    return list;
  }

  return fromStorage || [];
};

export const getRememberedAccount = () => getRememberedAccounts()[0] || null;

export const saveRememberedAccounts = (accounts) => {
  const cleaned = mergeByEmail((accounts || []).map(normalizeAccount).filter(Boolean)).slice(
    0,
    MAX_ACCOUNTS
  );
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(cleaned));
  } catch (_) {
    /* private mode / quota */
  }
  return cleaned;
};

/** Speichert E-Mail, Passwort und Mail-Server für schnellen Re-Login. */
export const saveRememberedAccount = (email, password, mail = {}, extras = {}) => {
  const account = normalizeAccount({
    email,
    password,
    mail,
    authType: extras.authType,
  });
  if (!account || !account.email) return getRememberedAccounts();

  account.lastUsedAt = new Date().toISOString();
  const existing = getRememberedAccounts().filter((a) => a.email !== account.email);
  // Wenn ohne Passwort gespeichert wird, altes Passwort behalten (außer Google)
  if (!account.password && account.authType !== 'google') {
    const prev = getRememberedAccounts().find((a) => a.email === account.email);
    if (prev?.password) account.password = prev.password;
  }
  if (account.authType === 'google') {
    account.password = '';
  }
  return saveRememberedAccounts([account, ...existing]);
};

export const removeRememberedAccount = (email, _imapHost) => {
  const key = accountKey(email);
  const next = getRememberedAccounts().filter((a) => a.email !== key);
  return saveRememberedAccounts(next);
};

export const clearRememberedAccount = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(LEGACY_KEY);
  } catch (_) {}
};

/** Entfernt Session-/User-Caches beim Logout oder Account-Wechsel. */
export const clearSensitiveClientData = () => {
  try {
    localStorage.removeItem('smtpConfig');
    localStorage.removeItem('inboxEmailsCache.v2');
    localStorage.removeItem('inboxEmailsCache.v1');
    localStorage.removeItem('mailPreferences');
    localStorage.removeItem('theme');
    const drop = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (
        key &&
        (key.startsWith('inboxEmailsCache.v3:') ||
          key.startsWith('inboxEmailsCache.v4:') ||
          key.startsWith('inboxEmailsCache.v5:'))
      ) {
        drop.push(key);
      }
    }
    drop.forEach((key) => localStorage.removeItem(key));
  } catch (_) {}
};

export const accountInitial = (email = '') => {
  const local = String(email).split('@')[0] || '?';
  return local.charAt(0).toUpperCase();
};

export const accountDisplayName = (email = '') => {
  const local = String(email).split('@')[0] || email;
  return local;
};
